/*************************************************************************
 *
 *  Copyright 2020 Adobe
 *  All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/
const getTranslatedPoint = (point, midpoint, angle) => {
  const { x, y } = point;
  const { x: tx, y: ty } = midpoint;
  const translatedPoint = {};
  if (
    typeof x !== "undefined" &&
    typeof y !== "undefined" &&
    typeof tx !== "undefined" &&
    typeof ty !== "undefined" &&
    typeof angle !== "undefined"
  ) {
    translatedPoint.x =
      Math.cos(-1 * angle) * (x - tx) - Math.sin(-1 * angle) * (y - ty) + tx;
    translatedPoint.y =
      Math.sin(-1 * angle) * (x - tx) + Math.cos(-1 * angle) * (y - ty) + ty;
  }
  return translatedPoint;
};

export const getRotationAngleFromQuad = (quad, inDegree = false) => {
  // pass in degree true to get angle in degrees
  const { tl, tr, bl, br } = quad;
  let angle = 0;
  let slope = 0;
  if (tl && tr && bl && br) {
    slope = (bl.y - tl.y) / (tl.x - bl.x);
    if (!isNaN(slope)) {
      angle = Math.PI / 2 - Math.atan(slope);
      if (inDegree) angle *= 180 / Math.PI;
    }
  }
  return angle;
};

export const getQuadWithoutTransformations = quadWithTransfomations => {
  let { tl, tr, bl, br } = quadWithTransfomations;
  const midPoint = {};
  let angle = getRotationAngleFromQuad(quadWithTransfomations);
  if (tl && tr && bl && br) {
    midPoint.x = tl.x / 4 + tr.x / 4 + bl.x / 4 + br.x / 4;
    midPoint.y = tl.y / 4 + tr.y / 4 + bl.y / 4 + br.y / 4;
    tl = getTranslatedPoint(tl, midPoint, angle);
    tr = getTranslatedPoint(tr, midPoint, angle);
    bl = getTranslatedPoint(bl, midPoint, angle);
    br = getTranslatedPoint(br, midPoint, angle);
  }
  const quad = { tl, tr, bl, br };
  angle *= 180 / Math.PI; // converting angle into degrees
  return { quad, angle };
};

/**
 * Function to form cursorInfo object
 * @param {object} transformedQuad - A transformed quad object in View space. If object is in PDF space, it needs to be converted before
 * being passed to this function.
 * @param {boolean} isCursorVisible - Whether cursor is visible
 * @returns {object} cursorInfo - An object containing coordinates of cursor, its angle and a boolean value
 * telling whether cursor is visible
 */
export const getCursorInfoObjectFromTransformedQuad = (
  transformedQuad,
  isCursorVisible
) => {
  const cursorInfo = {};
  let { tl, tr, bl, br } = transformedQuad;
  const translatedQuad = getQuadWithoutTransformations({ tl, tr, bl, br });
  const { angle, quad } = translatedQuad;
  tl = quad.tl;
  tr = quad.tr;
  bl = quad.bl;
  br = quad.br;
  if (tl && tr && bl && br && !isNaN(tl.x) && !isNaN(tl.y)) {
    cursorInfo.xmin = Math.min(tl.x, tr.x, bl.x, br.x);
    cursorInfo.xmax = Math.max(tl.x, tr.x, bl.x, br.x);
    cursorInfo.ymin = Math.min(tl.y, tr.y, bl.y, br.y);
    cursorInfo.ymax = Math.max(tl.y, tr.y, bl.y, br.y);
  }
  cursorInfo.angle = angle;
  cursorInfo.isVisible = isCursorVisible;
  return cursorInfo;
};
